import { useState, useEffect, useCallback } from 'react';
import { getInvoices } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { FileText, Search, Printer, Building2, CheckCircle2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const MEAL_PLAN_LABELS = {
  room_only: 'Room Only',
  breakfast: 'Breakfast',
  breakfast_lunch: 'Breakfast + Lunch',
  breakfast_dinner: 'Breakfast + Dinner',
  all_meals: 'All Meals',
};

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(amount || 0);

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-IN', {
    year: 'numeric', month: 'short', day: 'numeric',
  });
};

export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const response = await getInvoices();
      setInvoices(response.data);
    } catch {
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const viewInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setInvoiceModalOpen(true);
  };

  const printInvoice = () => window.print();

  const filtered = invoices.filter(inv => {
    const q = searchQuery.toLowerCase();
    return (inv.guest_name || '').toLowerCase().includes(q) ||
           (inv.invoice_number || '').toLowerCase().includes(q) ||
           (inv.booking_number || '').toLowerCase().includes(q);
  });

  return (
    <div data-testid="invoices-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-stone-900 font-['Manrope'] tracking-tight">Invoices</h1>
          <p className="text-stone-500 mt-1">Auto-generated on guest checkout with full pricing breakdown</p>
        </div>
      </div>

      <div className="flex gap-3 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <Input
            placeholder="Search by guest, invoice or booking #..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 border-stone-300"
            data-testid="search-invoices-input"
          />
        </div>
      </div>

      <div className="bg-white border border-stone-200 rounded-md overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sage-700"></div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <FileText className="w-12 h-12 mx-auto mb-3 text-stone-300" />
            <p className="text-stone-500">No invoices yet. Invoices are auto-generated on guest checkout.</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-stone-50 hover:bg-stone-50">
                <TableHead className="font-semibold text-stone-700">Invoice #</TableHead>
                <TableHead className="font-semibold text-stone-700">Generated</TableHead>
                <TableHead className="font-semibold text-stone-700">Guest</TableHead>
                <TableHead className="font-semibold text-stone-700">Booking</TableHead>
                <TableHead className="font-semibold text-stone-700">Stay</TableHead>
                <TableHead className="font-semibold text-stone-700">Total</TableHead>
                <TableHead className="font-semibold text-stone-700">Balance</TableHead>
                <TableHead className="font-semibold text-stone-700 text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((inv, index) => (
                <TableRow
                  key={inv.id}
                  className="table-row-hover animate-fade-in"
                  style={{ animationDelay: `${index * 0.03}s` }}
                  data-testid={`invoice-row-${inv.id}`}
                >
                  <TableCell className="font-mono text-xs text-sage-700">{inv.invoice_number}</TableCell>
                  <TableCell className="text-stone-600">{formatDate(inv.generated_at)}</TableCell>
                  <TableCell className="font-medium text-stone-900">{inv.guest_name}</TableCell>
                  <TableCell className="font-mono text-xs text-stone-600">{inv.booking_number}</TableCell>
                  <TableCell className="text-stone-600 text-sm">
                    {inv.check_in_date} → {inv.check_out_date}
                    <span className="block text-xs text-stone-400">{inv.nights} night{inv.nights > 1 ? 's' : ''}</span>
                  </TableCell>
                  <TableCell className="font-medium text-stone-900">{formatCurrency(inv.grand_total)}</TableCell>
                  <TableCell>
                    {inv.is_settled ? (
                      <span className="inline-flex items-center gap-1 text-xs text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
                        <CheckCircle2 className="w-3 h-3" /> Settled
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs text-sage-700 bg-sage-50 px-2 py-0.5 rounded-full">
                        <AlertCircle className="w-3 h-3" /> {formatCurrency(inv.balance_due)}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => viewInvoice(inv)}
                      className="h-8 px-3 border-stone-300"
                      data-testid={`view-invoice-${inv.id}`}
                    >
                      <FileText className="w-4 h-4 mr-1" />View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={invoiceModalOpen} onOpenChange={setInvoiceModalOpen}>
        <DialogContent className="bg-white sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="no-print">
            <DialogTitle className="font-['Manrope'] flex items-center justify-between">
              <span>Invoice</span>
              <Button
                variant="outline" size="sm" onClick={printInvoice}
                className="border-stone-300" data-testid="print-invoice-btn"
              >
                <Printer className="w-4 h-4 mr-2" />Print
              </Button>
            </DialogTitle>
          </DialogHeader>

          {selectedInvoice && (
            <div className="mt-4 print:mt-0" id="invoice-content">
              <div className="flex items-start justify-between border-b border-stone-200 pb-6 mb-6">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Building2 className="w-8 h-8 text-sage-700" />
                    <div>
                      <h2 className="text-xl font-bold text-stone-900 font-['Manrope']">Nomi-Connect</h2>
                      <p className="text-xs text-stone-500">Premium Hostel & Stay Services</p>
                    </div>
                  </div>
                  <p className="text-sm text-stone-600 mt-3">Sikkim, India</p>
                </div>
                <div className="text-right">
                  <h3 className="text-2xl font-bold text-stone-900 font-['Manrope']">INVOICE</h3>
                  <p className="text-sm text-stone-600 mt-2"><span className="font-medium">Invoice #:</span> {selectedInvoice.invoice_number}</p>
                  <p className="text-sm text-stone-600"><span className="font-medium">Booking #:</span> {selectedInvoice.booking_number}</p>
                  <p className="text-sm text-stone-600"><span className="font-medium">Generated:</span> {formatDate(selectedInvoice.generated_at)}</p>
                </div>
              </div>

              <div className="mb-6">
                <h4 className="text-sm font-semibold text-stone-500 uppercase tracking-wider mb-2">Bill To</h4>
                <p className="font-medium text-stone-900">{selectedInvoice.guest_name}</p>
                {selectedInvoice.guest_email && <p className="text-sm text-stone-600">{selectedInvoice.guest_email}</p>}
              </div>

              <div className="bg-stone-50 rounded-md p-4 mb-6 grid grid-cols-2 gap-4 text-sm">
                <div><p className="text-stone-500">Room</p><p className="font-medium text-stone-900">{selectedInvoice.room_number || '-'}</p></div>
                <div><p className="text-stone-500">Meal Plan</p><p className="font-medium text-stone-900">{MEAL_PLAN_LABELS[selectedInvoice.meal_plan] || selectedInvoice.meal_plan}</p></div>
                <div><p className="text-stone-500">Check-in</p><p className="font-medium text-stone-900">{selectedInvoice.check_in_date}</p></div>
                <div><p className="text-stone-500">Check-out</p><p className="font-medium text-stone-900">{selectedInvoice.check_out_date}</p></div>
              </div>

              <table className="w-full mb-6">
                <thead>
                  <tr className="border-b border-stone-200">
                    <th className="text-left py-2 text-sm font-semibold text-stone-700">Description</th>
                    <th className="text-right py-2 text-sm font-semibold text-stone-700">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-stone-100">
                    <td className="py-3 text-stone-900">
                      Room Accommodation
                      <span className="text-xs text-stone-500 block">{selectedInvoice.nights} night{selectedInvoice.nights > 1 ? 's' : ''} × {formatCurrency(selectedInvoice.effective_rate)}</span>
                    </td>
                    <td className="py-3 text-right font-medium text-stone-900">{formatCurrency(selectedInvoice.room_amount)}</td>
                  </tr>
                  {selectedInvoice.meal_amount > 0 && (
                    <tr className="border-b border-stone-100">
                      <td className="py-3 text-stone-900">
                        Meal Plan — {MEAL_PLAN_LABELS[selectedInvoice.meal_plan]}
                        <span className="text-xs text-stone-500 block">{formatCurrency(selectedInvoice.meal_rate_per_day)}/day × {selectedInvoice.nights} day{selectedInvoice.nights > 1 ? 's' : ''}</span>
                      </td>
                      <td className="py-3 text-right font-medium text-stone-900">{formatCurrency(selectedInvoice.meal_amount)}</td>
                    </tr>
                  )}
                  {selectedInvoice.fnb_amount > 0 && (
                    <tr className="border-b border-stone-100">
                      <td className="py-3 text-stone-900">F&amp;B Extras (Restaurant)</td>
                      <td className="py-3 text-right font-medium text-stone-900">{formatCurrency(selectedInvoice.fnb_amount)}</td>
                    </tr>
                  )}
                  <tr className="border-b border-stone-100">
                    <td className="py-2 text-stone-600 text-sm">Subtotal</td>
                    <td className="py-2 text-right text-stone-700">{formatCurrency(selectedInvoice.subtotal)}</td>
                  </tr>
                  {selectedInvoice.discount > 0 && (
                    <tr className="border-b border-stone-100">
                      <td className="py-2 text-emerald-700 text-sm">Coupon Discount {selectedInvoice.coupon_code ? `(${selectedInvoice.coupon_code})` : ''}</td>
                      <td className="py-2 text-right text-emerald-700">-{formatCurrency(selectedInvoice.discount)}</td>
                    </tr>
                  )}
                  <tr className="border-b border-stone-100">
                    <td className="py-2 text-stone-600 text-sm">GST ({selectedInvoice.gst_rate}%)</td>
                    <td className="py-2 text-right text-stone-700">{formatCurrency(selectedInvoice.gst_amount)}</td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-stone-300">
                    <td className="py-3 font-semibold text-stone-900">Grand Total</td>
                    <td className="py-3 text-right font-bold text-lg text-sage-700">{formatCurrency(selectedInvoice.grand_total)}</td>
                  </tr>
                  <tr>
                    <td className="py-1 text-sm text-stone-600">Total Paid</td>
                    <td className="py-1 text-right text-sm text-stone-700">{formatCurrency(selectedInvoice.total_paid)}</td>
                  </tr>
                  <tr>
                    <td className={`py-1 text-sm font-semibold ${selectedInvoice.is_settled ? 'text-emerald-700' : 'text-sage-700'}`}>Balance Due</td>
                    <td className={`py-1 text-right text-sm font-semibold ${selectedInvoice.is_settled ? 'text-emerald-700' : 'text-sage-700'}`}>{formatCurrency(selectedInvoice.balance_due)}</td>
                  </tr>
                </tfoot>
              </table>

              <div className="border-t border-stone-200 pt-6 text-center">
                <p className="text-sm text-stone-500">Thank you for staying with Nomi-Connect!</p>
                <p className="text-xs text-stone-400 mt-1">This is a computer-generated invoice.</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <style>{`
        @media print {
          body * { visibility: hidden; }
          #invoice-content, #invoice-content * { visibility: visible; }
          #invoice-content { position: absolute; left: 0; top: 0; width: 100%; padding: 20px; }
          .no-print { display: none !important; }
        }
      `}</style>
    </div>
  );
}
